import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import chartOfAccountsApi from '../../api/chartOfAccountsApi';
import glSettingsApi from '../../api/glSettingsApi';
import SearchableSelect from '../../components/ui/SearchableSelect';

export default function GlSettingsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  
  const [settings, setSettings] = useState({
    salesAccountId: '',
    salesReturnAccountId: '',
    discountAllowedAccountId: '',
    
    purchasesAccountId: '',
    purchasesReturnAccountId: '',
    discountReceivedAccountId: '',
    
    inventoryAccountId: '',
    costOfGoodsSoldAccountId: '',
    inventoryAdjustmentAccountId: '',
    
    vatOutputAccountId: '',
    vatInputAccountId: '',

    cashShortageAccountId: '',
    cashOverageAccountId: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Postable Accounts (Level 5)
      const accRes = await chartOfAccountsApi.getPostable();
      setAccounts(accRes.data || []);

      // 2. Fetch Current Settings
      const setRes = await glSettingsApi.get();
      if (setRes.data) {
        // Ensure nulls are converted to empty strings for inputs
        const s = setRes.data;
        const cleanSettings = {};
        // Only map keys that exist in our state to avoid errors
        Object.keys(settings).forEach(key => {
            cleanSettings[key] = s[key] || '';
        });
        setSettings(cleanSettings);
      }
    } catch (error) {
      console.error(error);
      // Don't show error if it's 404 (just means no settings yet)
      if (error.response && error.response.status !== 404) {
          toast.error("فشل تحميل البيانات");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Convert empty strings to null
      const payload = {};
      Object.keys(settings).forEach(key => {
        payload[key] = settings[key] === '' ? null : settings[key];
      });

      await glSettingsApi.update(payload);
      toast.success("تم حفظ الإعدادات بنجاح");
    } catch (error) {
      console.error(error);
      toast.error("فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  // Helper to render select
  const renderSelect = (label, name, filterFn = null) => {
    let filteredAccounts = accounts;
    if (filterFn) {
        filteredAccounts = accounts.filter(filterFn);
    }

    return (
      <div className="mb-4">
        <SearchableSelect
          label={label}
          name={name}
          options={filteredAccounts}
          value={settings[name]}
          onChange={(val) => handleChange(name, val)}
          getOptionLabel={(option) => `${option.code} - ${option.nameAr}`}
          getOptionValue={(option) => option.id}
        />
      </div>
    );
  };

  if (loading) return <div className="p-8 text-center">جاري التحميل...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto bg-white rounded-lg shadow-md mt-6">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h1 className="text-2xl font-bold text-gray-800">إعدادات توجيه الحسابات (الربط المحاسبي)</h1>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
        >
          {saving ? "جاري الحفظ..." : "حفظ التغييرات"}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* Sales Section */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h2 className="text-lg font-semibold text-blue-700 mb-4 border-b pb-2">المبيعات والعملاء</h2>
          {renderSelect("حساب المبيعات (الإيراد)", "salesAccountId", a => a.accountType === 4)}
          {renderSelect("حساب مردود المبيعات", "salesReturnAccountId", a => a.accountType === 4 || a.accountType === 5)}
          {renderSelect("حساب الخصم المسموح به", "discountAllowedAccountId", a => a.accountType === 5 || a.accountType === 4)}
          {renderSelect("حساب ضريبة المبيعات (VAT Output)", "vatOutputAccountId", a => a.accountType === 2)}
        </div>

        {/* Purchases Section */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h2 className="text-lg font-semibold text-blue-700 mb-4 border-b pb-2">المشتريات والموردين</h2>
          {renderSelect("حساب المشتريات (للنظام الدوري) / المخزون (للمستمر)", "purchasesAccountId")} 
          {/* Note: usually Purchases is Expense (5) or Asset (1) depending on system */}
          {renderSelect("حساب مردود المشتريات", "purchasesReturnAccountId", a => a.accountType === 5 || a.accountType === 1 || a.accountType === 4)}
          {renderSelect("حساب الخصم المكتسب", "discountReceivedAccountId", a => a.accountType === 4 || a.accountType === 5)}
          {renderSelect("حساب ضريبة المشتريات (VAT Input)", "vatInputAccountId", a => a.accountType === 1 || a.accountType === 2)}
        </div>

        {/* Inventory Section */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h2 className="text-lg font-semibold text-blue-700 mb-4 border-b pb-2">المخزون وتكلفة البضاعة</h2>
          {renderSelect("حساب المخزون الافتراضي", "inventoryAccountId", a => a.accountType === 1)}
          {renderSelect("حساب تكلفة البضاعة المباعة (COGS)", "costOfGoodsSoldAccountId", a => a.accountType === 5)}
          {renderSelect("حساب تسوية المخزون", "inventoryAdjustmentAccountId", a => a.accountType === 5)}
        </div>

        {/* POS Variance Section */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h2 className="text-lg font-semibold text-blue-700 mb-4 border-b pb-2">فروقات الصناديق (نقاط البيع)</h2>
          {/* Allow broader account types for variance: Expense (5), Asset (1) for Shortage; Revenue (4), Liability (2) for Overage */}
          {renderSelect("حساب العجز النقدي (Expense/Asset)", "cashShortageAccountId", a => a.accountType === 5 || a.accountType === 1)}
          {renderSelect("حساب الزيادة النقدية (Revenue/Liability)", "cashOverageAccountId", a => a.accountType === 4 || a.accountType === 2)}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-sm text-blue-800">
          <h3 className="font-bold mb-2">ملاحظات هامة:</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>يتم استخدام هذه الحسابات كقيم افتراضية عند إنشاء الفواتير والسندات.</li>
            <li>يمكن تجاوز حسابات "المخزون" و "الصناديق" و "البنوك" من خلال تعريفاتها الخاصة.</li>
            <li>تأكد من اختيار الحسابات من المستوى الأخير (القابلة للقيد) فقط.</li>
          </ul>
        </div>

      </form>
    </div>
  );
}
